import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle, MapPin, Calendar, Ruler } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';

// Case Study Data (would typically come from an API or data file)
const caseStudies = {
  'aubuchon-hardware': {
    title: 'Aubuchon Hardware',
    industry: 'Retail',
    location: 'Multiple Locations',
    timeline: 'Ongoing Partnership',
    squareFootage: 'Multi-location rollout',
    heroImage: '/Assets/images/case-studies/aubuchon-hardware-hero.jpg',
    overview: 'Aubuchon Hardware partnered with Portico Systems to upgrade their retail flooring across multiple store locations, requiring durable commercial flooring systems that could withstand heavy retail foot traffic and equipment use.',
    challenge: 'Aubuchon Hardware needed commercial flooring systems that could handle the demands of a hardware retail environment — heavy foot traffic, equipment carts, dropped tools, and constant cleaning. The flooring also needed to support entry/transition zones and maintain a professional appearance across multiple store locations.',
    solution: 'Portico Systems provided a comprehensive flooring program including durable retail-ready surfaces and entry/transition solutions tailored to each store layout. The structured rollout ensured consistent quality and timely delivery across all locations.',
    materialsUsed: ['Commercial flooring systems', 'Durable retail-ready surfaces', 'Entry and transition solutions', 'High-performance commercial matting'],
    results: [
      'Durable flooring installed across multiple store locations',
      'Consistent quality and appearance maintained',
      'Entry and transition zones properly addressed',
      'Ongoing trusted partnership established'
    ],
    galleryImages: [
      '/Assets/images/case-studies/aubuchon-hardware-hero.jpg',
      '/Assets/images/case-studies/aubuchon_aisles_lvt.jpg',
      '/Assets/images/case-studies/aubuchon_entry_mat.jpg',
      '/Assets/images/case-studies/aubuchon_battery_aisle.jpg',
      '/Assets/images/case-studies/aubuchon_fasteners_aisle.jpg',
      '/Assets/images/case-studies/aubuchon_office_carpet.jpg'
    ]
  },
  'sky-zone': {
    title: 'Sky Zone, Greenville SC',
    industry: 'Commercial',
    location: 'Greenville, SC USA',
    timeline: 'Completed Project',
    squareFootage: 'Full facility',
    heroImage: '/Assets/images/case-studies/sky-zone-hero.jpg',
    overview: 'Sky Zone in Greenville, SC needed flooring capable of handling high foot traffic without compromising appearance, with an installation process that minimized disruption to business operations.',
    challenge: 'The facility needed flooring that could handle heavy foot traffic while maintaining a professional appearance. The old floors were not making the right impression on clients. The installation required minimal disruption to ongoing business operations, and the solution needed to match professional standards with minimal maintenance requirements.',
    solution: 'Portico Systems provided Recycled Rubber flooring with commercial-grade adhesives and complete floor preparation and leveling. The installation was completed efficiently with minimal business disruption, transforming the space to match professional standards.',
    materialsUsed: ['Recycled Rubber flooring', 'Commercial-grade adhesives', 'Floor preparation and leveling materials'],
    results: [
      'Modern, professional appearance that enhances the space',
      'Durable flooring solution for high-traffic use',
      'Reduced maintenance time and costs',
      'Efficient installation with minimal business disruption'
    ],
    galleryImages: [
      '/Assets/images/case-studies/sky-zone-hero.jpg',
      '/Assets/images/case-studies/sky_zone_dodgeball.jpg',
      '/Assets/images/case-studies/sky_zone_walkway.jpg',
      '/Assets/images/case-studies/sky_zone_skyslam.jpg',
      '/Assets/images/case-studies/sky_zone_arena_view.jpg',
      '/Assets/images/case-studies/sky_zone_stair_landing.jpg'
    ],
    testimonial: {
      quote: "We needed flooring that could handle heavy traffic while making the right impression on clients — our old floors were holding us back. Working with Portico, we upgraded to Recycled Rubber flooring that's transformed our space into something that finally matches our professional standards, handles everything we throw at it, and requires almost no maintenance.",
      author: "The team at Sky Zone",
      title: "Greenville, SC"
    }
  }
};

// Default case study for unknown slugs
const defaultCaseStudy = {
  title: 'Project Case Study',
  industry: 'Commercial',
  location: 'Various Locations',
  timeline: 'Varies',
  squareFootage: 'Contact for details',
  heroImage: '/Assets/images/industries/corporate-offices.jpg',
  overview: 'This case study showcases a successful custom flooring installation project completed by Portico Systems.',
  challenge: 'Each project presents unique challenges requiring customized solutions.',
  solution: 'Our team develops tailored approaches to meet specific client needs.',
  materialsUsed: ['Custom flooring solutions', 'Branded entry systems', 'Premium materials'],
  results: ['Successful project completion', 'Client satisfaction', 'Quality craftsmanship'],
  galleryImages: ['/Assets/images/industries/corporate-offices.jpg']
};

const CaseStudyPage = () => {
  const { slug } = useParams();
  const caseStudy = caseStudies[slug] || defaultCaseStudy;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px]" data-testid="case-study-hero">
        <div className="absolute inset-0">
          <img
            src={caseStudy.heroImage}
            alt={caseStudy.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
        </div>
        <div className="container-portico relative h-full flex flex-col justify-end pb-12">
          <Link to="/custom/portfolio" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Portfolio
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-3 py-1 bg-secondary text-white text-sm font-semibold rounded-full mb-4">
              {caseStudy.industry}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {caseStudy.title}
            </h1>
            <div className="flex flex-wrap gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {caseStudy.location}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {caseStudy.timeline}
              </div>
              <div className="flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                {caseStudy.squareFootage}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-6">Overview</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {caseStudy.overview}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-6">The Challenge</h2>
              <p className="text-muted-foreground leading-relaxed">
                {caseStudy.challenge}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-6">Our Solution</h2>
              <p className="text-muted-foreground leading-relaxed">
                {caseStudy.solution}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Materials Used */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-6">Materials Used</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseStudy.materialsUsed.map((material) => (
                  <li key={`material-${material}`} className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                    <span>{material}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold">Project Gallery</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {caseStudy.galleryImages.map((image, index) => (
              <motion.div
                key={image}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="aspect-video rounded-xl overflow-hidden"
              >
                <img
                  src={image}
                  alt={`${caseStudy.title} - Image ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding bg-primary-bg text-white">
        <div className="container-portico">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">Results</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {caseStudy.results.map((result, index) => (
                <motion.div
                  key={`result-${index}-${result.slice(0, 20)}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-white/10 rounded-lg"
                >
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span>{result}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-background">
        <div className="container-portico text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let's discuss how we can deliver similar results for your organization.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/custom#consultation-form">
                <Button size="lg">
                  Request Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/custom/portfolio">
                <Button size="lg" variant="outline">
                  View More Projects
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default CaseStudyPage;
